// ============================================================================
// Settings & Access — RBAC user management + the audit-log viewer.
//   Team & Access: list users, change role (inline select) / status (disable),
//     and add users. Management controls render only for admins.
//   Audit log: filter by action, page through, and export CSV — mirrors the
//     pagination + export UX of the HoneyTrap module.
// Talks to /api/users (GET/POST/PATCH) and /api/audit (GET, paginated).
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'
import { etDT } from '../lib/format.js'

const ROLE_OPTS = ['viewer', 'agent', 'manager', 'admin']

// api.js exposes get/post/del only — PATCH goes straight through fetch.
async function patchUser(body) {
  const res = await fetch('/api/users', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  })
  const ct = res.headers.get('content-type') || ''
  const data = ct.includes('application/json') ? await res.json() : await res.text()
  if (!res.ok) throw new Error((data && data.error) || res.statusText)
  return data
}

export function renderSettings(el) {
  el.innerHTML = `
    <div class="grid" style="gap:18px">
      <div class="toolbar">
        <button class="btn ghost" id="tabTeam">Team &amp; Access</button>
        <button class="btn ghost" id="tabAudit">Audit log</button>
      </div>
      <div id="paneTeam"></div>
      <div id="paneAudit" style="display:none"></div>
    </div>`

  const $ = (id) => el.querySelector('#' + id)
  let isAdmin = false

  // ---- Tab switching ----
  function showTab(which) {
    const team = which === 'team'
    $('paneTeam').style.display = team ? '' : 'none'
    $('paneAudit').style.display = team ? 'none' : ''
    $('tabTeam').style.opacity = team ? '1' : '.6'
    $('tabAudit').style.opacity = team ? '.6' : '1'
  }
  $('tabTeam').addEventListener('click', () => showTab('team'))
  $('tabAudit').addEventListener('click', () => showTab('audit'))
  showTab('team')

  // ==========================================================================
  // TEAM & ACCESS
  // ==========================================================================
  function renderTeamPane() {
    $('paneTeam').innerHTML = `
      <div class="card pad">
        <div class="mod-h">Team &amp; access</div>
        <div class="mod-sub">${isAdmin
          ? 'Manage roles and access. You cannot disable or demote your own account.'
          : 'Read-only — ask an admin to change roles or access.'}</div>
        <div class="table-wrap">
          <table class="h-table">
            <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Last login</th></tr></thead>
            <tbody id="uRows"><tr><td colspan="5" class="empty">Loading…</td></tr></tbody>
          </table>
        </div>
        <div class="msg" id="uMsg"></div>
      </div>
      ${isAdmin ? `
      <div class="card pad" style="margin-top:18px">
        <div class="mod-h">Add user</div>
        <div class="mod-sub">Create a new account with an initial role and password.</div>
        <div class="form-grid">
          <div class="field"><label>Email</label><input class="h-input" id="nEmail" placeholder="name@company.com" /></div>
          <div class="field"><label>Name</label><input class="h-input" id="nName" placeholder="Full name" /></div>
          <div class="field"><label>Role</label>
            <select class="h-input" id="nRole">
              ${ROLE_OPTS.map(r => `<option value="${r}"${r === 'agent' ? ' selected' : ''}>${r}</option>`).join('')}
            </select>
          </div>
          <div class="field"><label>Password</label><input class="h-input" id="nPass" type="password" placeholder="At least 6 characters" /></div>
          <div class="field"><label>&nbsp;</label><button class="btn" id="nAdd">Add user</button></div>
        </div>
        <div class="msg" id="nMsg"></div>
      </div>` : ''}`
  }

  async function loadUsers() {
    const rows = $('uRows')
    if (!rows) return
    rows.innerHTML = '<tr><td colspan="5" class="empty">Loading…</td></tr>'
    try {
      const { users = [] } = await api.get('/api/users')
      rows.innerHTML = users.length ? users.map(u => `
        <tr>
          <td>${esc(u.email)}</td>
          <td>${esc(u.name || '—')}</td>
          <td>${isAdmin
            ? `<select class="h-input" data-role-id="${esc(u.id)}" style="width:auto">
                 ${ROLE_OPTS.map(r => `<option value="${r}"${r === u.role ? ' selected' : ''}>${r}</option>`).join('')}
               </select>`
            : `<span class="pill">${esc(u.role)}</span>`}</td>
          <td>${u.status === 'disabled'
            ? '<span class="pill mk">disabled</span>'
            : '<span class="pill an">active</span>'}
            ${isAdmin
            ? `<button class="btn ghost" data-status-id="${esc(u.id)}" data-status="${u.status === 'disabled' ? 'active' : 'disabled'}" style="padding:5px 12px;margin-left:8px">${u.status === 'disabled' ? 'Enable' : 'Disable'}</button>`
            : ''}</td>
          <td class="num">${u.last_login ? esc(etDT(u.last_login)) : '—'}</td>
        </tr>`).join('') : '<tr><td colspan="5" class="empty">No users.</td></tr>'

      if (isAdmin) {
        rows.querySelectorAll('select[data-role-id]').forEach(s =>
          s.addEventListener('change', () => updateUser({ id: s.dataset.roleId, role: s.value })))
        rows.querySelectorAll('button[data-status-id]').forEach(b =>
          b.addEventListener('click', () => updateUser({ id: b.dataset.statusId, status: b.dataset.status })))
      }
    } catch (e) { rows.innerHTML = `<tr><td colspan="5" class="empty">Error: ${esc(e.message)}</td></tr>` }
  }

  async function updateUser(body) {
    const msg = $('uMsg'); if (msg) { msg.className = 'msg'; msg.textContent = '' }
    try {
      await patchUser(body)
      if (msg) { msg.className = 'msg ok'; msg.textContent = 'Updated.' }
      loadUsers()
    } catch (e) {
      if (msg) { msg.className = 'msg bad'; msg.textContent = e.message }
      loadUsers() // revert inline control to server truth
    }
  }

  function wireAddUser() {
    const btn = $('nAdd')
    if (!btn) return
    btn.addEventListener('click', async () => {
      const msg = $('nMsg'); msg.className = 'msg'; msg.textContent = ''
      const email = $('nEmail').value.trim()
      const password = $('nPass').value
      if (!email || !password) { msg.className = 'msg bad'; msg.textContent = 'Email and password required.'; return }
      try {
        await api.post('/api/users', { email, name: $('nName').value.trim(), role: $('nRole').value, password })
        msg.className = 'msg ok'; msg.textContent = 'User added.'
        $('nEmail').value = ''; $('nName').value = ''; $('nPass').value = ''
        loadUsers()
      } catch (e) { msg.className = 'msg bad'; msg.textContent = e.message }
    })
  }

  // ==========================================================================
  // AUDIT LOG
  // ==========================================================================
  $('paneAudit').innerHTML = `
    <div class="card pad">
      <div class="mod-h">Audit log</div>
      <div class="mod-sub">The immutable record of every privileged action, in Eastern time.</div>
      <div class="toolbar">
        <input class="h-input grow" id="aFilter" placeholder="Filter by action…" />
        <select class="h-input" id="aSize" style="width:auto">
          <option value="25">25 / page</option>
          <option value="50" selected>50 / page</option>
          <option value="100">100 / page</option>
        </select>
        <button class="btn ghost" id="aExport">Export CSV</button>
      </div>
      <div class="table-wrap">
        <table class="h-table">
          <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Target</th><th>IP</th></tr></thead>
          <tbody id="aRows"><tr><td colspan="5" class="empty">Loading…</td></tr></tbody>
        </table>
      </div>
      <div class="toolbar" style="margin-top:12px;margin-bottom:0">
        <span class="mod-sub" id="aCount" style="margin:0"></span>
        <span class="grow"></span>
        <button class="btn ghost" id="aPrev">‹ Prev</button>
        <span class="mod-sub" id="aPage" style="margin:0"></span>
        <button class="btn ghost" id="aNext">Next ›</button>
      </div>
    </div>`

  let pageSize = 50, page = 0, total = 0

  async function loadAudit() {
    const rows = $('aRows'), action = $('aFilter').value.trim()
    rows.innerHTML = '<tr><td colspan="5" class="empty">Loading…</td></tr>'
    try {
      const r = await api.get(`/api/audit?limit=${pageSize}&offset=${page * pageSize}${action ? '&action=' + encodeURIComponent(action) : ''}`)
      const list = r.entries || []
      total = r.total || 0
      rows.innerHTML = list.length ? list.map(e => `
        <tr>
          <td class="num">${e.ts ? esc(etDT(e.ts)) : '—'}</td>
          <td>${esc(e.action || '—')}</td>
          <td>${esc(e.actor_email || '—')}</td>
          <td>${esc(e.target || e.entity_id || '—')}</td>
          <td>${esc(e.ip || '—')}</td>
        </tr>`).join('') : '<tr><td colspan="5" class="empty">No audit entries.</td></tr>'
      updatePager(list.length)
    } catch (e) { rows.innerHTML = `<tr><td colspan="5" class="empty">Error: ${esc(e.message)}</td></tr>` }
  }

  function updatePager(shown) {
    const from = total === 0 ? 0 : page * pageSize + 1
    const to = page * pageSize + shown
    const pages = Math.max(Math.ceil(total / pageSize), 1)
    $('aCount').textContent = `Showing ${from}–${to} of ${total}`
    $('aPage').textContent = `Page ${page + 1} of ${pages}`
    $('aPrev').disabled = page <= 0
    $('aNext').disabled = page >= pages - 1
    $('aPrev').style.opacity = page <= 0 ? '.5' : '1'
    $('aNext').style.opacity = page >= pages - 1 ? '.5' : '1'
  }

  function toCsv(rows) {
    const cols = ['ts', 'action', 'actor_email', 'entity_type', 'entity_id', 'target', 'ip', 'meta']
    const cell = (r, c) => {
      let v = r[c] == null ? '' : String(r[c])
      if (c === 'ts' && r[c]) v = new Date(r[c]).toISOString()
      if (/^[=+\-@\t\r]/.test(v)) v = "'" + v; return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
    }
    return [cols.join(','), ...rows.map(r => cols.map(c => cell(r, c)).join(','))].join('\n')
  }

  $('aExport').addEventListener('click', async () => {
    const action = $('aFilter').value.trim()
    const btn = $('aExport'); const label = btn.textContent; btn.textContent = 'Exporting…'; btn.disabled = true
    try {
      const r = await api.get(`/api/audit?limit=100000${action ? '&action=' + encodeURIComponent(action) : ''}`)
      const csv = toCsv(r.entries || [])
      const blob = new Blob([csv], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `hive-audit-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(a.href)
    } catch (e) { alert('Export failed: ' + e.message) }
    finally { btn.textContent = label; btn.disabled = false }
  })

  $('aSize').addEventListener('change', () => { pageSize = parseInt($('aSize').value) || 50; page = 0; loadAudit() })
  $('aPrev').addEventListener('click', () => { if (page > 0) { page--; loadAudit() } })
  $('aNext').addEventListener('click', () => { page++; loadAudit() })
  let ft
  $('aFilter').addEventListener('input', () => { clearTimeout(ft); ft = setTimeout(() => { page = 0; loadAudit() }, 250) })

  // ---- Boot: resolve role, then wire the team pane + load both sections ----
  ;(async () => {
    try { const me = await api.me(); isAdmin = me.role === 'admin' } catch {}
    renderTeamPane()
    wireAddUser()
    loadUsers()
  })()
  loadAudit()
}
